import type React from 'react'

/**
 * "Preparing your document for signature" indicator (GEN-5027).
 *
 * Shown on the order page in the window between submitting an embedded-eligible
 * order and its envelope reaching `sent` — the envelope is created post-commit
 * and best-effort, so there is a gap where the signature request does not exist
 * yet or is still `created`. Once the status reaches `sent`, the page stops
 * rendering this and the signature badge (and, per GEN-5028, the Sign TRF
 * affordance) take over. `OrderInProgress` owns that decision via
 * `trfSignaturePreparing`; this component only draws the state.
 *
 * Styled to match `TrfSignatureBadge`'s pending tone so the transition from
 * "preparing" to "TRF Ready for signature" reads as one control changing, not
 * two different elements swapping.
 */

interface TrfSignaturePreparingProps {
  className?: string
}

export const TrfSignaturePreparing: React.FC<TrfSignaturePreparingProps> = ({ className = '' }) => {
  return (
    <div
      data-testid="trf-signature-preparing"
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-amber-800 ${className}`.trim()}
    >
      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <span className="text-sm font-medium">Preparing your document for signature</span>
    </div>
  )
}

export default TrfSignaturePreparing
